import { MsalProvider, useIsAuthenticated } from '@azure/msal-react';
import { QueryClientProvider, useSuspenseQuery } from '@tanstack/react-query';
import { ErrorComponent, RouterProvider, createRouter } from '@tanstack/react-router';
import { Spinner } from '@volvo/vcdk-react';
import i18n from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { initReactI18next } from 'react-i18next';

import deTranslation from './assets/locales/de/translation';
import enTranslation from './assets/locales/en/translation';
import frTranslation from './assets/locales/fr/translation';
import itTranslation from './assets/locales/it/translation';
import nlTranslation from './assets/locales/nl/translation';
import ptTranslation from './assets/locales/pt/translation';
import svTranslation from './assets/locales/sv/translation';
import { msalInstance } from './config/MsalAuthConfig';
import { routeTree } from './routeTree.gen';

import { languageKey } from '@/config/EnviromentConfig';
import { queryClient } from '@/config/QueryConfig';
import EnvironmentContextProvider from '@/contexts/environment';
import { FLEET_STORAGE_KEY } from '@/contexts/fleet-account/FleetAccountContext';
import { fleetAccountsQueryOptions } from '@/service/vpc/queryOptions';

document.documentElement.setAttribute('lang', localStorage.getItem(languageKey) || 'en');

let currentLng = localStorage.getItem(languageKey);
const defaultLng = 'en';

if (!currentLng) {
  localStorage.setItem(languageKey, defaultLng);
  currentLng = defaultLng;
}

const resources = {
  en: {
    translation: enTranslation,
  },
  sv: {
    translation: svTranslation,
  },
  pt: {
    translation: ptTranslation,
  },
  de: {
    translation: deTranslation,
  },
  fr: {
    translation: frTranslation,
  },
  nl: {
    translation: nlTranslation,
  },
  it: {
    translation: itTranslation,
  },
} as const;

i18n
  .use(initReactI18next) // passes i18n down to react-i18next
  .init({
    resources,
    debug: true,
    lng: currentLng || defaultLng, // if you're using a language detector, do not define the lng option
    fallbackLng: 'en',
    returnNull: false,
    interpolation: {
      escapeValue: false,
    },
  });

// Create the router instance
const router = createRouter({
  routeTree,
  basepath: import.meta.env.VITE_BASE_PATH || '/',
  defaultPendingComponent: () => (
    <div className="flex h-full w-full flex-col items-center justify-center">
      <Spinner size="medium" className="" />
    </div>
  ),
  defaultErrorComponent: ({ error }) => <ErrorComponent error={error} />,
  context: {
    fleetAccountId: undefined!,
    queryClient: queryClient,
  },
  defaultPreload: 'intent',
  // Since we're using React Query, we don't want loader calls to ever be stale
  // This will ensure that the loader is always called when the route is preloaded or visited
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
  // Use simple URLSearchParams instead of JSON serialization to avoid double-quoting
  stringifySearch: (search) => {
    const params = new URLSearchParams();
    Object.entries(search).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.set(key, String(value));
      }
    });
    const searchStr = params.toString();
    return searchStr ? `?${searchStr}` : '';
  },
  parseSearch: (searchStr) => {
    const params = new URLSearchParams(searchStr);
    const search: Record<string, string> = {};
    params.forEach((value, key) => {
      search[key] = value;
    });
    return search;
  },
  defaultNotFoundComponent: () => <div>Page Not Found (defaultNotFoundComponent)</div>,
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function AuthenticatedApp() {
  // Fetch fleet accounts with suspense - will wait for data before rendering
  const { data: fleetAccounts } = useSuspenseQuery(fleetAccountsQueryOptions());

  const isValidFleetAccountId = useCallback(
    (fleetId: string | null) => {
      if (!fleetId) {
        return false;
      }

      return fleetAccounts.some((fleetAccount) => fleetAccount.fleetAccountId === fleetId);
    },
    [fleetAccounts]
  );

  // Track localStorage changes for fleet account selection
  const [selectedFleetId, setSelectedFleetId] = useState<string | null>(() => {
    const storedFleetId = localStorage.getItem(FLEET_STORAGE_KEY);

    if (isValidFleetAccountId(storedFleetId)) {
      return storedFleetId;
    }

    if (storedFleetId) {
      localStorage.removeItem(FLEET_STORAGE_KEY);
    }

    return null;
  });

  // Listen for fleet account changes
  useEffect(() => {
    const handleFleetAccountChange = (e: CustomEvent<string | null>) => {
      const nextFleetId = e.detail;

      if (isValidFleetAccountId(nextFleetId)) {
        setSelectedFleetId(nextFleetId);
      } else {
        localStorage.removeItem(FLEET_STORAGE_KEY);
        setSelectedFleetId(null);
      }
    };

    window.addEventListener('fleetAccountChanged', handleFleetAccountChange as EventListener);

    return () => {
      window.removeEventListener('fleetAccountChanged', handleFleetAccountChange as EventListener);
    };
  }, [isValidFleetAccountId]);

  const resolvedSelectedFleetId = useMemo(() => {
    if (isValidFleetAccountId(selectedFleetId)) {
      return selectedFleetId;
    }

    return null;
  }, [selectedFleetId, isValidFleetAccountId]);

  const selectedFleetAccount = useMemo(() => {
    try {
      if (!resolvedSelectedFleetId || !fleetAccounts) return undefined;
      return fleetAccounts.find((fa) => fa.fleetAccountId === resolvedSelectedFleetId);
    } catch {
      return undefined;
    }
  }, [fleetAccounts, resolvedSelectedFleetId]);

  return (
    <RouterProvider
      router={router}
      context={{
        fleetAccountId: selectedFleetAccount?.fleetAccountId,
        queryClient,
      }}
    />
  );
}

function InnerApp() {
  const isAuthenticated = useIsAuthenticated();
  const [sessionKey, setSessionKey] = useState(0);

  useEffect(() => {
    const handler = () => setSessionKey((k) => k + 1);
    window.addEventListener('vpc-session-reset', handler);
    return () => window.removeEventListener('vpc-session-reset', handler);
  }, []);

  // Only fetch and wait for fleet accounts when authenticated
  if (isAuthenticated) {
    return <AuthenticatedApp key={sessionKey} />;
  }

  // For unauthenticated users (login page), render router without fleet account context
  return (
    <RouterProvider
      router={router}
      context={{
        fleetAccountId: undefined,
        queryClient,
      }}
    />
  );
}

function App() {
  return (
    <div className="vcdk-theme-volvo h-dvh antialiased">
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <EnvironmentContextProvider>
            <InnerApp />
          </EnvironmentContextProvider>
        </QueryClientProvider>
      </MsalProvider>
    </div>
  );
}

export default App;
